import { GetPartFinderBatchResponse, JobStatus } from '@/api/generatedApi';
import { PART_FINDER_ACTIVITY_QUERY_KEY } from '@/hooks/api/part-finder-activity/query';
import { PART_FINDER_BATCHES_QUERY_KEY, PART_FINDER_BATCH_QUERY_KEY } from '@/hooks/api/part-finder-batches/query';
import { useGetPartFinderBatch } from '@/hooks/api/part-finder-batches/useGetPartFinderBatch';
import { PART_FINDER_REPORTS_QUERY_KEY } from '@/hooks/api/part-finder-reports/query';
import { AxiosApiError } from '@/types/apiError';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

const PART_FINDER_BATCH_POLL_INTERVAL_MS = 3000;

const isInProgress = (status: JobStatus | undefined): boolean =>
  status === JobStatus.PENDING || status === JobStatus.RUNNING;

export const usePartFinderBatchPolling = (
  batchId: string,
  initialStatus?: JobStatus
): { data?: GetPartFinderBatchResponse; isLoading: boolean; error: AxiosApiError | null } => {
  const queryClient = useQueryClient();
  const wasInProgressReference = useRef(isInProgress(initialStatus ?? JobStatus.PENDING));
  const lastRefreshSignatureReference = useRef<string | undefined>(undefined);

  const { data, isLoading, error } = useGetPartFinderBatch(batchId, {
    refetchInterval: query =>
      isInProgress(query.state.data?.partFinderBatch.status) ? PART_FINDER_BATCH_POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    if (!data) return;
    const batch = data.partFinderBatch;
    const inProgress = isInProgress(batch.status);
    if (inProgress || wasInProgressReference.current) {
      const refreshSignature = `${batch.status}:${batch.progress}:${JSON.stringify(batch.breakdown)}`;
      if (refreshSignature !== lastRefreshSignatureReference.current) {
        lastRefreshSignatureReference.current = refreshSignature;
        queryClient.resetQueries({ queryKey: [PART_FINDER_REPORTS_QUERY_KEY], exact: false });
        queryClient.invalidateQueries({ queryKey: [PART_FINDER_ACTIVITY_QUERY_KEY], exact: false });
      }
    }
    if (!inProgress && wasInProgressReference.current) {
      wasInProgressReference.current = false;
      queryClient.invalidateQueries({ queryKey: [PART_FINDER_BATCH_QUERY_KEY], exact: false });
      queryClient.invalidateQueries({ queryKey: [PART_FINDER_BATCHES_QUERY_KEY], exact: false });
    }
  }, [data, queryClient]);

  return { data, isLoading, error };
};
